import { useAuth, useOrganization, useUser } from '@clerk/clerk-react';
import { SupportProvider } from '@flobi/asset-library/support';
import '@flobi/asset-library/support.css';
import { useMemo, type ReactNode } from 'react';
import { useTheme } from '../theme';

/** The support service. Empty: no widget at all (the kit draws nothing without an address). */
const SUPPORT_URL = String(import.meta.env.VITE_SUPPORT_URL || '').replace(/\/+$/, '');
const TURNSTILE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || undefined;

/**
 * Flobi Support (the kit's widget), mounted once at the root over Home and the editor alike: no
 * button of its own, so it opens from the team's banner, a ?support= link, or a chat left open in
 * another app. Artwork's look, in the theme the page is in. Signed out (the view page), the kit's
 * guest form. The kit starts nothing inside a frame.
 */
export function ArtworkSupport({ children }: { children: ReactNode }) {
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { theme } = useTheme();

  const id = isSignedIn ? user?.id || null : null;
  const firstName = user?.firstName || '';
  const fullName = user?.fullName || '';
  const who = useMemo(() => (id ? { id, firstName, fullName } : null), [id, firstName, fullName]);
  const orgId = organization?.id || null;
  const orgName = organization?.name || '';
  const workspace = useMemo(() => (orgId ? { id: orgId, name: orgName } : null), [orgId, orgName]);

  if (!SUPPORT_URL) return <>{children}</>;
  return (
    <SupportProvider
      app="artwork"
      url={SUPPORT_URL}
      getToken={id ? getToken : undefined}
      user={who}
      theme="artwork"
      mode={theme === 'light' ? 'light' : 'dark'}
      workspace={workspace}
      turnstileSiteKey={TURNSTILE_KEY}
    >
      {children}
    </SupportProvider>
  );
}
